import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

interface AvatarContextType {
  emoji: string | null;
  color: string | null;
  setEmoji: (emoji: string) => void;
  setColor: (color: string) => void;
  loadAvatar: () => Promise<void>;
}

const AvatarContext = createContext<AvatarContextType | undefined>(undefined);

export function AvatarProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [emoji, setEmojiState] = useState<string | null>(localStorage.getItem('emojiAvatar'));
  const [color, setColorState] = useState<string | null>(null);

  const persistToDb = useCallback(async (newEmoji: string | null, newColor: string | null) => {
    if (!user) return;
    await supabase.from('profiles').upsert(
      { user_id: user.id, avatar_emoji: newEmoji, avatar_color: newColor },
      { onConflict: 'user_id' }
    );
  }, [user]);

  const setEmoji = useCallback((e: string) => {
    setEmojiState(e);
    localStorage.setItem('emojiAvatar', e);
    // Persist with current color
    setColorState(prev => { persistToDb(e, prev); return prev; });
  }, [persistToDb]);

  const setColor = useCallback((c: string) => {
    setColorState(c);
    // Persist with current emoji
    setEmojiState(prev => { persistToDb(prev, c); return prev; });
  }, [persistToDb]);

  const loadAvatar = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('avatar_emoji, avatar_color')
      .eq('user_id', user.id)
      .maybeSingle();

    if (data) {
      const e = data.avatar_emoji ?? localStorage.getItem('emojiAvatar');
      setEmojiState(e);
      setColorState(data.avatar_color ?? null);
      if (data.avatar_emoji) localStorage.setItem('emojiAvatar', data.avatar_emoji);
    } else {
      setEmojiState(localStorage.getItem('emojiAvatar'));
      setColorState(null);
    }
  }, [user]);

  return (
    <AvatarContext.Provider value={{ emoji, color, setEmoji, setColor, loadAvatar }}>
      {children}
    </AvatarContext.Provider>
  );
}

export function useAvatar() {
  const context = useContext(AvatarContext);
  if (!context) throw new Error('useAvatar must be used within AvatarProvider');
  return context;
}
